import { CollectionType } from 'app/queries/collections/collection-fragment'
import {
  CollectionProps,
  createCollectionQuery,
  PaginatedCollection,
  typeToName,
} from 'app/queries/collections/single'
import {
  lastVideoQuery,
  mapToLastVideoData,
} from 'app/queries/collections/last-video'
import { useStores } from 'app/store'
import { useEffect, useMemo, useRef } from 'react'
import usePagination from 'app/hooks/use-pagination'
import useQuery from 'app/hooks/use-query'

export default function useSeriesLanding(
  type: CollectionType,
  slug: string,
  sort: string = '-publishedAt'
) {
  const { lastCollectionStore, userStore } = useStores()

  const collectionQuery = useMemo(() => createCollectionQuery(type), [type])

  const variables = useMemo(() => {
    return { [typeToName(type)]: slug, sort }
  }, [slug, sort, type])

  const pagination = usePagination(collectionQuery.query, variables, {
    limit: 12,
    formatFn: collectionQuery.mapToPage,
  })

  // Fetch last watched video for "Continue Watching" / "Start Watching" button
  const lastVideoQueryString = useMemo(() => lastVideoQuery(), [])
  const { data: lastVideo } = useQuery(
    lastVideoQueryString,
    { slug },
    mapToLastVideoData,
    { enabled: !!slug && !!userStore?.user }
  )

  const { collection: currentCollection, featured } = (pagination?.data ||
    {}) as PaginatedCollection

  // Keep a stable reference to collection metadata so it doesn't disappear on sort change
  const collectionRef = useRef<CollectionProps | undefined>(undefined)
  if (currentCollection) {
    collectionRef.current = currentCollection
  }

  // Use the ref if current data is loading, otherwise use current data
  const collection = currentCollection || collectionRef.current

  useEffect(() => {
    if (collection && lastCollectionStore) {
      lastCollectionStore.setCollectionId(collection.id)
      lastCollectionStore.setCollectionType(type)
      lastCollectionStore.setCollectionSlug(slug)
      lastCollectionStore.setCollectionName(collection.title)
      lastCollectionStore.setIsPrimaryCollection(false)
    }
  }, [collection, type, slug, lastCollectionStore])

  return { collection, featured, pagination, lastVideo }
}
